import { useCallback, useEffect, useState } from "react";
import type { AuthCredentials } from "../interface/auth_types";
import SocketMqttBridge from "../interface/socket_mqtt_bridge";
import useAuthenticatedCredentials from "../store/hook/useAuthenticatedCredentials";
import {
  clearStoredSession,
  persistSession,
  readStoredCredentials,
} from "./auth/session_storage";

type TransportLoginResult = {
  signaling: PromiseSettledResult<void>;
  mqtt: PromiseSettledResult<void>;
};

type TransportLoginOptions = {
  persist: boolean;
  clearStoredSessionOnFailure: boolean;
};

type UseTransportLoginParams = {
  bridge: SocketMqttBridge;
};

const EMPTY_CREDENTIALS: AuthCredentials = {
  userId: "",
  password: "",
};

const LOGIN_FAILED_MESSAGE = "로그인에 실패했습니다.";

class TransportLoginError extends Error {
  result: TransportLoginResult;

  constructor(result: TransportLoginResult) {
    super(LOGIN_FAILED_MESSAGE);
    this.name = "TransportLoginError";
    this.result = result;
  }
}

function getRejectedMessage(result: PromiseSettledResult<void>): string | null {
  if (result.status !== "rejected") {
    return null;
  }

  const reason = result.reason;
  if (reason instanceof Error) {
    return reason.message;
  }
  return String(reason);
}

function formatTransportLoginError(result: TransportLoginResult): string {
  const messages = [
    getRejectedMessage(result.signaling),
    getRejectedMessage(result.mqtt),
  ].filter(Boolean);
  return messages.join(" / ") || "Transport login failed";
}

async function authenticateVehicleTransports(
  credentials: AuthCredentials,
  bridge: SocketMqttBridge,
): Promise<TransportLoginResult> {
  bridge.setCredentials(credentials);

  const [signaling, mqtt] = await Promise.allSettled([
    bridge.loginSignaling(),
    bridge.loginMqtt(),
  ]);
  const result = { signaling, mqtt };

  if (signaling.status === "rejected" && mqtt.status === "rejected") {
    throw new TransportLoginError(result);
  }

  return result;
}


export function useTransportLogin(params: UseTransportLoginParams) {
  const { bridge } = params;
  const {
    authenticatedCredentials,
    setAuthenticatedCredentials,
    clearAuthenticatedCredentials,
  } = useAuthenticatedCredentials();
  const [credentials, setCredentials] = useState<AuthCredentials>({
    userId: authenticatedCredentials?.userId ?? "",
    password: authenticatedCredentials?.password ?? "",
  });
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);

  useEffect(() => {
    if (!authenticatedCredentials) {
      return;
    }

    setCredentials(authenticatedCredentials);
    setIsLoggingIn(false);
    setLoginError(null);
  }, [authenticatedCredentials]);

  const resetLoginState = useCallback(() => {
    setCredentials(EMPTY_CREDENTIALS);
    setIsLoggingIn(false);
    setLoginError(null);
    clearAuthenticatedCredentials();
  }, [clearAuthenticatedCredentials]);

  const login = useCallback(
    (
      nextCredentials: AuthCredentials,
      options: TransportLoginOptions = {
        persist: true,
        clearStoredSessionOnFailure: false,
      },
    ) => {
      setIsLoggingIn(true);
      setLoginError(null);

      void authenticateVehicleTransports(nextCredentials, bridge)
        .then(() => {
          if (options.persist) {
            persistSession(nextCredentials);
          }
          setCredentials(nextCredentials);
          setIsLoggingIn(false);
          setLoginError(null);
          setAuthenticatedCredentials(nextCredentials);
        })
        .catch((err) => {
          if (options.clearStoredSessionOnFailure) {
            clearStoredSession();
          }
          setIsLoggingIn(false);
          if (err instanceof TransportLoginError) {
            console.error(
              "[useTransportLogin] Transport login failed",
              formatTransportLoginError(err.result),
            );
            setLoginError(LOGIN_FAILED_MESSAGE);
            return;
          }
          console.error("[useTransportLogin] Transport login failed", err);
          setLoginError(LOGIN_FAILED_MESSAGE);
        });
    },
    [bridge, setAuthenticatedCredentials],
  );

  const restoreStoredSession = useCallback(() => {
    if (authenticatedCredentials) {
      return;
    }

    const storedCredentials = readStoredCredentials();
    if (!storedCredentials) {
      return;
    }

    setCredentials(storedCredentials);
    login(storedCredentials, {
      persist: false,
      clearStoredSessionOnFailure: true,
    });
  }, [authenticatedCredentials, login]);

  return {
    authenticatedCredentials,
    credentials,
    setCredentials,
    isLoggingIn,
    loginError,
    login,
    restoreStoredSession,
    clearStoredSession,
    resetLoginState,
  };
}
